/**
 * Progression Storage — coin economy and unlock persistence via localStorage.
 *
 * Follows the same pattern as src/skins/storage.ts:
 * JSON serialize/deserialize, try-catch for localStorage errors, read-modify-write.
 */

import { TANK_PRICES, COLOR_PRICE } from './catalog';

const STORAGE_KEY = 'geometry-blast-progression';

interface ProgressionData {
  coins: number;
  unlocks: string[]; // e.g. ['tank:scatter', 'color:15736371']
}

// ─── Core Persistence ─────────────────────────────────────────────────────────

export function loadProgression(): ProgressionData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { coins: 0, unlocks: [] };
    return JSON.parse(raw) as ProgressionData;
  } catch {
    return { coins: 0, unlocks: [] };
  }
}

export function saveProgression(data: ProgressionData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage full or unavailable — silently fail
  }
}

// ─── Coins ────────────────────────────────────────────────────────────────────

export function getCoins(): number {
  return loadProgression().coins;
}

/** Add coins and persist. Returns the new total. */
export function addCoins(amount: number): number {
  const data = loadProgression();
  data.coins += amount;
  saveProgression(data);
  return data.coins;
}

// ─── Unlocks ──────────────────────────────────────────────────────────────────

export function hasUnlock(key: string): boolean {
  const data = loadProgression();
  return data.unlocks.includes(key);
}

/**
 * Attempt to purchase an unlock. Deducts price from coins if affordable.
 * Returns true if purchase succeeded, false if insufficient funds or already owned.
 */
function purchaseUnlock(key: string, price: number): boolean {
  const data = loadProgression();
  if (data.unlocks.includes(key)) return false; // already owned
  if (data.coins < price) return false;
  data.coins -= price;
  data.unlocks.push(key);
  saveProgression(data);
  return true;
}

// ─── Tank Helpers ─────────────────────────────────────────────────────────────

/** Check if a tank is unlocked. Default tanks (not in TANK_PRICES) are always unlocked. */
export function isTankUnlocked(tankId: string): boolean {
  if (!(tankId in TANK_PRICES)) return true;
  return hasUnlock('tank:' + tankId);
}

/** Get the price for a locked tank, or 0 if it's a default tank. */
export function getTankPrice(tankId: string): number {
  return TANK_PRICES[tankId] ?? 0;
}

/** Attempt to purchase a tank. Returns true on success. */
export function purchaseTank(tankId: string): boolean {
  const price = TANK_PRICES[tankId];
  if (!price) return false;
  return purchaseUnlock('tank:' + tankId, price);
}

// ─── Color Helpers ────────────────────────────────────────────────────────────

/** Check if a skin color is unlocked. Pass the set of default tank body colors as free. */
export function isColorUnlocked(colorHex: number, defaultTankColors: number[]): boolean {
  if (defaultTankColors.includes(colorHex)) return true;
  return hasUnlock('color:' + colorHex);
}

/** Attempt to purchase a color. Returns true on success. */
export function purchaseColor(colorHex: number): boolean {
  return purchaseUnlock('color:' + colorHex, COLOR_PRICE);
}
